/**
 * The behaviour of the wallet's organization spending-limit meters (wallet part 2a, 2026-10-02).
 *
 * Moved verbatim out of src/components/wallet/WalletSpendMeters.tsx so today's strip draws from
 * it and the new design's wallet draws from the same hook: the pooled windows are read only for a
 * viewer allowed to see them, a failed read shows nothing (the strip is garnish, never a wall), and
 * each window's figures and how full it is are worked out once here.
 */
import { useEffect, useState } from 'react';
import { PoolWindowStatus, getAccountSpendLimits } from '../services/rbacService';
import { useUsageVisibility } from './useUsageVisibility';

/** How full a window is: under 80% is fine, up to 95% is near, past that is over. */
export type MeterTone = 'ok' | 'near' | 'over';

export interface SpendMeter {
  id: string;
  label: string;
  /** "$12.00 of $50.00 · 24%". */
  figures: string;
  /** How much of the limit is used, 0–100, for the bar's width. */
  fillPct: number;
  /** The rounded percent the figures and the screen-reader label say. */
  percent: number;
  tone: MeterTone;
}

const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function meterTone(ratio: number): MeterTone {
  const pct = ratio * 100;
  if (pct < 80) return 'ok';
  if (pct <= 95) return 'near';
  return 'over';
}

export function spendMeterOf(s: PoolWindowStatus): SpendMeter {
  const ratio = Number.isFinite(s.ratio) ? Math.max(0, s.ratio) : 0;
  const percent = Math.round(ratio * 100);
  return {
    id: s.id,
    label: s.label,
    figures: `${usd(s.usedUsd)} of ${usd(s.limitUsd)} · ${percent}%`,
    fillPct: Math.min(100, ratio * 100),
    percent,
    tone: meterTone(ratio),
  };
}

/** The meters to draw; an empty list means draw nothing. */
export function useWalletSpendMeters(): SpendMeter[] {
  const { canViewPoolLimits } = useUsageVisibility();
  const [statuses, setStatuses] = useState<PoolWindowStatus[]>([]);

  useEffect(() => {
    if (!canViewPoolLimits) return;
    let canceled = false;
    getAccountSpendLimits()
      .then((res) => {
        if (!canceled) setStatuses(res.windowsStatus ?? []);
      })
      .catch(() => {
        /* strip simply doesn't render */
      });
    return () => {
      canceled = true;
    };
  }, [canViewPoolLimits]);

  if (!canViewPoolLimits) return [];
  return statuses.map(spendMeterOf);
}
